import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  AlertTriangle, ArrowLeft, RefreshCw, RotateCcw, Loader2,
  CheckCircle, XCircle, Eye
} from 'lucide-react';

export default function FiscalErros() {
  const [loading, setLoading] = useState(true);
  const [erros, setErros] = useState([]);
  const [reprocessando, setReprocessando] = useState({});
  
  const navigate = useNavigate();

  useEffect(() => {
    loadErros();
  }, []);

  const loadErros = async () => {
    setLoading(true);
    try {
      const response = await api.get('/fiscal/erros');
      setErros(response.data.erros || []);
    } catch (error) {
      toast.error('Erro ao carregar notas com erro');
    } finally {
      setLoading(false);
    }
  };

  const handleReprocessar = async (notaId) => {
    setReprocessando(prev => ({ ...prev, [notaId]: true }));
    try {
      await api.post(`/fiscal/notas/${notaId}/reprocessar`);
      toast.success('Nota adicionada à fila de reprocessamento');
      loadErros();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reprocessar nota');
    } finally {
      setReprocessando(prev => ({ ...prev, [notaId]: false }));
    }
  };

  const handleReprocessarTodos = async () => {
    const notasComErro = erros.filter(e => ['erro', 'rejeitada'].includes(e.status));
    
    if (notasComErro.length === 0) {
      toast.info('Nenhuma nota para reprocessar');
      return;
    }

    let sucesso = 0;
    let falhas = 0;

    for (const nota of notasComErro) {
      try {
        await api.post(`/fiscal/notas/${nota.id}/reprocessar`);
        sucesso++;
      } catch {
        falhas++;
      }
    }

    toast.success(`${sucesso} nota(s) adicionada(s) à fila. ${falhas > 0 ? `${falhas} falha(s).` : ''}`);
    loadErros();
  };

  const getStatusBadge = (status) => {
    if (status === 'erro') {
      return (
        <Badge className="bg-red-100 text-red-700 gap-1">
          <XCircle className="w-3 h-3" />
          Erro
        </Badge>
      );
    }
    return (
      <Badge className="bg-orange-100 text-orange-700 gap-1">
        <AlertTriangle className="w-3 h-3" />
        Rejeitada
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-erros">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/fiscal')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            Erros de Emissão
          </h1>
          <p className="text-muted-foreground">Notas que falharam e precisam de atenção</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadErros}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          {erros.length > 0 && (
            <Button onClick={handleReprocessarTodos}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reprocessar Todos
            </Button>
          )}
        </div>
      </div>

      {/* Tabela de erros */}
      <Card>
        <CardHeader>
          <CardTitle>Notas com Erro ({erros.length})</CardTitle>
          <CardDescription>
            Clique em "Reprocessar" para tentar novamente a emissão
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : erros.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <p className="text-muted-foreground">Nenhuma nota com erro.</p>
              <p className="text-sm text-muted-foreground mt-2">
                Todas as notas foram processadas com sucesso!
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Tomador</TableHead>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Competência</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Erro</TableHead>
                    <TableHead>Tentativas</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {erros.map(nota => (
                    <TableRow key={nota.id}>
                      <TableCell>
                        {getStatusBadge(nota.status)}
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        {nota.tomador_nome || '-'}
                      </TableCell>
                      <TableCell className="max-w-[120px] truncate">
                        {nota.aluno_nome || '-'}
                      </TableCell>
                      <TableCell>
                        {nota.competencia_mes?.toString().padStart(2, '0')}/{nota.competencia_ano}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        R$ {(nota.valor_servicos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <p className="text-sm text-red-600 truncate" title={nota.erro_mensagem}>
                          {nota.erro_mensagem || 'Erro não especificado'}
                        </p>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{nota.tentativas || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => navigate('/fiscal/notas')}
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleReprocessar(nota.id)}
                            disabled={reprocessando[nota.id]}
                            title="Reprocessar"
                          >
                            {reprocessando[nota.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dicas de resolução */}
      {erros.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-800">Dicas para Resolução</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-blue-700 text-sm">
            <p>
              <strong>Erro de conexão:</strong> Verifique a conectividade com a prefeitura 
              na página de Integração.
            </p>
            <p>
              <strong>Dados inválidos:</strong> Confira se o CPF/CNPJ do tomador está correto 
              e se todos os campos obrigatórios foram preenchidos.
            </p>
            <p>
              <strong>Certificado expirado:</strong> Verifique a validade do certificado digital 
              nas Configurações.
            </p>
            <p>
              <strong>Limite excedido:</strong> Algumas prefeituras têm limite de emissões por 
              período. Aguarde e tente novamente.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
